// MCIP-19 — the backend-incident surface: a slim red banner strip when active
// incident groups exist, and a pod-wise panel with copy-ready AI drafts.
//
// Two rules inherited from the rest of the app:
//  - the banner NEVER renders without active groups (bannerLabel returns null),
//  - the AI draft is TEXT for a human to copy — nothing here posts to Slack;
//    the future MCIP-15 dispatch posts behind the existing guardrail.
import { useState } from 'react';
import { api } from '../lib/api';
import { pollAnalysis } from '../lib/aiPoll';
import { bannerLabel, clusterDeepLink, totalsLabel } from '../lib/incidents';

/** Same copy-button pattern as FixCard: idle | copied | failed, self-resetting. */
function CopyButton({ text, label = 'Copy' }) {
  const [state, setState] = useState('idle');
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch {
      setState('failed');
    }
    setTimeout(() => setState('idle'), 1600);
  }
  return (
    <button type="button" className="ghost-btn" onClick={copy} aria-live="polite">
      {state === 'copied' ? 'Copied ✓' : state === 'failed' ? 'Select & copy manually' : label}
    </button>
  );
}

/** Red strip at the top of the dashboard. Renders nothing when all is quiet. */
export function IncidentBanner({ incidents, region = null, environment = null, onView, onDismiss }) {
  const label = bannerLabel(incidents, { region, environment });
  if (!label) return null;
  return (
    <div className="incident-banner" role="alert">
      <button type="button" className="incident-banner__view" onClick={onView}>
        {label}
      </button>
      {onDismiss && (
        <button
          type="button"
          className="incident-banner__dismiss"
          aria-label="Dismiss incident banner for this session"
          title="Dismiss for this session — incidents stay on the Incidents screen"
          onClick={onDismiss}
        >
          ×
        </button>
      )}
    </div>
  );
}

/** One pod group: header (pod, totals, channel), cluster rows, the draft flow. */
function IncidentGroup({ group, escalationChannel }) {
  const [draftState, setDraftState] = useState('idle'); // idle | loading | done | error
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);

  async function runDraft() {
    setDraftState('loading'); setError(null);
    try {
      // Async-poll contract, same as the fix card's Glean analysis: the POST
      // answers 202 while Glean runs server-side; the probe rides it home.
      const force = draftState === 'done';
      const result = await pollAnalysis({
        start: () => api.incidentDraft(group.pod, { force }),
        probe: () => api.incidentDraftStatus(group.pod),
        fetchResult: () => api.incidentDraft(group.pod),
      });
      setDraft(result); setDraftState('done');
    } catch (e) {
      setError(e); setDraftState('error');
    }
  }

  return (
    <div className="incident-group">
      <div className="incident-group__head">
        <div>
          <h3 className="card__title">{group.pod}</h3>
          <p className="card__subtitle">
            {totalsLabel(group.totals)} · suggest posting in{' '}
            <span className="tag">{group.suggested_channel}</span>
            {/* MCIP-24: the release(s) the anomaly was observed on, from the
                affected runs — absent when no stored row carries a version. */}
            {group.deploy_versions?.length > 0 && (
              <>
                {' '}· observed on{' '}
                {group.deploy_versions.map((v) => (
                  <span key={v} className="tag tag--ver" title="release at failure">{v}</span>
                ))}
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          className="primary-btn"
          disabled={draftState === 'loading'}
          onClick={runDraft}
        >
          {draftState === 'loading' ? 'Drafting… (1–2 min)'
            : draftState === 'done' ? 'Re-draft report'
              : 'Draft incident report'}
        </button>
      </div>

      <ul className="incident-clusters">
        {group.clusters.map((c) => {
          const link = clusterDeepLink(c);
          return (
            <li key={c.cluster_id} className="incident-cluster">
              <span className={`tag tag--bug`}>{c.failure_category}</span>
              {link
                ? <a href={link} className="incident-cluster__title">{c.title}</a>
                : <span className="incident-cluster__title">{c.title}</span>}
              <span className="muted">
                {c.member_count} failure{c.member_count === 1 ? '' : 's'}
                {c.affected_jobs?.length > 0 && <> · {c.affected_jobs.join(', ')}</>}
                {c.last_seen_at && <> · last seen {new Date(c.last_seen_at).toLocaleString()}</>}
              </span>
            </li>
          );
        })}
      </ul>

      {draftState === 'loading' && (
        <p className="ai__note"><span className="ai-spin" aria-hidden="true" /> Glean is drafting the incident report from the evidence…</p>
      )}
      {draftState === 'error' && (
        <p className={`ai__note ${error?.status === 503 ? '' : 'ai__note--error'}`}>
          {error?.status === 503
            ? 'AI drafting is not configured — set GLEAN_TOKEN on the API (Glean admin → Client API Tokens).'
            : `Draft failed: ${error?.message}`}
        </p>
      )}
      {draftState === 'done' && draft && (
        <div className="incident-draft">
          <p className="card__subtitle">
            post in <span className="tag">{draft.channel}</span> · escalate to{' '}
            <span className="tag">{draft.escalation_channel || escalationChannel}</span> —
            copy below; nothing is posted automatically
          </p>
          <div className="prompt__head">
            <h4 className="detail__h3">Thread title</h4>
            <CopyButton text={draft.thread_title || ''} label="Copy title" />
          </div>
          <pre className="incident-draft__title">{draft.thread_title}</pre>
          <div className="prompt__head">
            <h4 className="detail__h3">Thread description</h4>
            <CopyButton text={draft.thread_description || ''} label="Copy description" />
          </div>
          <pre className="ai__text">{draft.thread_description}</pre>
          {draft.citations?.length > 0 && (
            <ul className="ai__citations">
              {draft.citations.map((c) => (
                <li key={c.url}><a href={c.url} target="_blank" rel="noreferrer">{c.title}</a></li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** The incidents panel — pod-wise breakdown of active backend incidents. */
export default function IncidentsPanel({ incidents }) {
  if (!incidents || incidents.groups?.length === 0) return null;
  return (
    <section className="card incidents" id="incidents">
      <div className="card__head">
        <div>
          <h2 className="card__title">Active backend incidents</h2>
          <p className="card__subtitle">
            SERVICE_5XX / GRPC_BREAK clusters in the last {incidents.window_hours}h,
            grouped by pod · staging escalation: <span className="tag">{incidents.escalation_channel}</span>
          </p>
        </div>
      </div>
      {incidents.groups.map((g) => (
        <IncidentGroup key={g.pod} group={g} escalationChannel={incidents.escalation_channel} />
      ))}
    </section>
  );
}
